import {IDisposable} from '@mintdata/mint-kit/api/resources/IDisposable';

export type AnEventListener<T> = (e:T) => (void);
export type CancelableListener<T> = (e:T) => (boolean);
export type IsCancelled = boolean;
export type IsProcessActions = boolean;

export class CancelableBroadcaster<T>
{
    private _listeners:CancelableListener<T>[] = [];

    addListener(listener:CancelableListener<T>):IDisposable
    {
        this._listeners.push(listener);

        return {
            dispose: () =>
            {
                this.removeListener(listener);
            }
        };
    }

    removeListener(listener:CancelableListener<T>)
    {
        let idx = this._listeners.indexOf(listener);

        if( idx === -1 )
            throw new Error('Listener not found: ' + listener);

        this._listeners.splice(idx, 1);
    }

    fireCancelableEvent(event:T):IsCancelled
    {
        return this._dispatchEvent(event, this._listeners);
    }

    //noinspection JSMethodCanBeStatic
    private _dispatchEvent(event:T, listeners:CancelableListener<T>[]):IsCancelled
    {
        let didAnyListenerRequestCancel = false;
        let len = listeners.length;

        for( let i = 0; i < len; i++ )
        {
            let returnValue = listeners[i](event);

            if( returnValue )
                didAnyListenerRequestCancel = true;
        }

        return didAnyListenerRequestCancel;
    }
}

export class EventBroadcaster<T>
{
    private _listeners:AnEventListener<T>[] = [];
    private _typedListeners:{} = {}; //Map<string,EventListenerT[]>

    addTypedListener(eventType:string, listener:AnEventListener<T>)
    {
        let listeners = this._getOrCreateTypedListenerMap(eventType);
        listeners.push(listener);
    }

    removeTypedListener(T:string, listener:AnEventListener<T>)
    {
        let listeners = this._getOrCreateTypedListenerMap(T);

        let idx = listeners.indexOf(listener);

        if( idx === -1 )
            throw new Error('Typed listener not found (T: ' + T + '): ' + listener);

        listeners.splice(idx, 1);
    }

    private _getOrCreateTypedListenerMap(T:string):AnEventListener<T>[]
    {
        let listeners:AnEventListener<T>[] = (this._typedListeners as any)[T];

        if( !listeners )
            listeners = (this._typedListeners as any)[T] = [];

        return listeners;
    }

    addListener(listener:AnEventListener<T>):IDisposable
    {
        this._listeners.push(listener);

        return {
            dispose: () =>
            {
                this.removeListener(listener);
            }
        };
    }

    removeListener(listener:AnEventListener<T>)
    {
        let idx = this._listeners.indexOf(listener);

        if( idx === -1 )
            throw new Error('Listener not found: ' + listener);

        this._listeners.splice(idx, 1);
    }

    fireEvent(event:T):void
    {
        let typedListeners = (this._typedListeners as any)[(event as any).type];

        if( typedListeners )
            this._dispatchEvent(event, typedListeners);

        this._dispatchEvent(event, this._listeners);
    }

    fireEventAsync(event:T):void
    {
        setTimeout(() =>
        {
            let typedListeners = (this._typedListeners as any)[(event as any).type];

            if( typedListeners )
                this._dispatchEvent(event, typedListeners);

            this._dispatchEvent(event, this._listeners);
        });
    }

    //noinspection JSMethodCanBeStatic
    private _dispatchEvent(event:T, listeners:AnEventListener<T>[]):void
    {
        let len = listeners.length;

        for( let i = 0; i < len; i++ )
            listeners[i](event);
    }
}

export class NonPassiveEventListener
{
    public constructor(public el:HTMLElement,
                       public type:string,
                       public listener:EventListenerOrEventListenerObject,
                       public options?:AddEventListenerOptions)
    {

    }
}

export class NonPassiveEventListeners
{
    private static readonly _SINGLETON = new NonPassiveEventListeners();

    private _listeners:NonPassiveEventListener[] = [];

    public static getInstance():NonPassiveEventListeners
    {
        return this._SINGLETON;
    }

    private _supportsPassive():boolean
    {
        // Test via a getter in the options object to see if the passive property is accessed
        let supportsPassive = false;
        try
        {
            const opts = Object.defineProperty({}, 'passive', {
                get: function()
                {
                    supportsPassive = true;
                }
            });
            window.addEventListener('testPassive', null, opts);
            window.removeEventListener('testPassive', null, opts);
        }
        catch( e )
        {
        }

        return supportsPassive;
    }

    public add(listener:NonPassiveEventListener):IDisposable
    {
        listener.options = listener.options || {};

        if( this._supportsPassive() )
            listener.options = {...listener.options, passive: false};

        listener.el.addEventListener(listener.type, listener.listener, listener.options as any);
        this._listeners.push(listener);

        return {
            dispose: () => this.remove(listener)
        };
    }

    public remove(listener:NonPassiveEventListener):void
    {
        const index:number = this._listeners.indexOf(listener);

        if( index !== -1 )
        {
            const [removed] = this._listeners.splice(index, 1);

            removed.el.removeEventListener(removed.type, removed.listener);
        }
    }

    public postponeAll()
    {
        this._listeners.forEach((listener:NonPassiveEventListener) => (
            listener.el.removeEventListener(listener.type, listener.listener)
        ));
    }

    public restoreAll()
    {
        this._listeners.forEach((listener:NonPassiveEventListener) => (
            listener.el.addEventListener(listener.type, listener.listener, listener.options as any)
        ));
    }
}
